// Pure helpers that turn the leads list's URL into filters and filters into
// API queries / URL params. Shared by every view of the list (leadViews.js)
// so the list request and the export request are built in one place.

export const AD_FILTER_KEYS = ['adId', 'adsetId', 'campaignId', 'adPlatform'];

/** customFields[<key>]=value params as { key: value }. */
export function customFieldsFromUrl(searchParams) {
  const out = {};
  for (const [k, v] of searchParams.entries()) {
    const m = k.match(/^customFields\[(.+)\]$/);
    if (m && v) out[m[1]] = v;
  }
  return out;
}

/**
 * Filters for a view from the URL. `withAdFilters: false` leaves the ad
 * attribution drill-down keys out.
 */
export function parseFiltersFromUrl(searchParams, view, { withAdFilters = true } = {}) {
  const filters = {
    search: searchParams.get('search') || '',
    status: searchParams.get('status') || '',
    stageId: searchParams.get('stageId') || '',
    source: searchParams.get('source') || '',
    priority: searchParams.get('priority') || '',
  };
  if (withAdFilters) {
    for (const k of AD_FILTER_KEYS) filters[k] = searchParams.get(k) || '';
  }
  // View-specific URL filters (e.g. Customers: wonFrom / wonTo), and the
  // keys a view ignores (they never count as an active filter).
  for (const k of view.extraFilters || []) filters[k] = searchParams.get(k) || '';
  for (const k of view.ignoredFilters || []) filters[k] = '';
  filters.page = parseInt(searchParams.get('page')) || 1;
  filters.limit = parseInt(searchParams.get('limit')) || 10;
  filters.sortBy = searchParams.get('sortBy') || view.defaultSort.sortBy;
  filters.sortOrder = searchParams.get('sortOrder') || view.defaultSort.sortOrder;
  filters.customFields = customFieldsFromUrl(searchParams);
  return filters;
}

/**
 * API query for the list (or, with forExport, the export: no paging or
 * sort), with custom fields flattened into customFields[<key>] params that
 * qs decodes as a nested object on the server, then scoped by the view.
 */
export function toApiQuery(filters, view, { forExport = false } = {}) {
  const { customFields: cfObj, ...rest } = filters;
  const query = { ...rest };
  if (forExport) {
    delete query.page;
    delete query.limit;
    delete query.sortBy;
    delete query.sortOrder;
  }
  if (cfObj && typeof cfObj === 'object') {
    for (const [k, v] of Object.entries(cfObj)) {
      if (v === '' || v === undefined || v === null) continue;
      query[`customFields[${k}]`] = v;
    }
  }
  return view.scopeQuery(query);
}

/** URL params for filters; empty values are left out. */
export function filtersToUrlParams(filters) {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (k === 'customFields') {
      if (v && typeof v === 'object') {
        for (const [ck, cv] of Object.entries(v)) {
          if (cv) params.set(`customFields[${ck}]`, cv);
        }
      }
      continue;
    }
    if (v) params.set(k, v);
  }
  return params;
}
